import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fadeUp, waLink } from "@/lib/site-utils";
import { BOOKING_OPTIONS, IMAGES } from "@/data/site";

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/20 focus:border-jcb outline-none py-3 font-hind text-[#f5f5f0] placeholder:text-neutral-500 transition-colors duration-200";

export const Booking = () => {
  const [form, setForm] = useState({ name: "", phone: "", service: "", date: "", place: "", details: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service) {
      toast.error("कृपया नाम, फ़ोन और सेवा भरें / Fill name, phone & service");
      return;
    }
    const msg =
      `*नई बुकिंग / New Booking — SS Group*\n\n` +
      `नाम / Name: ${form.name}\n` +
      `फ़ोन / Phone: ${form.phone}\n` +
      `सेवा / Service: ${form.service}\n` +
      (form.date ? `तारीख / Date: ${form.date}\n` : "") +
      (form.place ? `स्थान / Place: ${form.place}\n` : "") +
      (form.details ? `विवरण / Details: ${form.details}\n` : "");
    toast.success("WhatsApp खुल रहा है… / Opening WhatsApp");
    window.open(waLink(msg), "_blank");
    setForm({ name: "", phone: "", service: "", date: "", place: "", details: "" });
  };

  return (
    <section id="booking" data-testid="booking-section" className="relative border-t border-white/10 py-24 md:py-36 overflow-hidden">
      <img src={IMAGES.sandstone} alt="Rajasthan sandstone texture" className="absolute inset-0 h-full w-full object-cover opacity-[0.06]" />
      <div className="relative mx-auto max-w-[1600px] px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-start">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <span className="font-plex text-xs tracking-[0.25em] uppercase text-jcb">ऑनलाइन बुकिंग / BOOK ONLINE</span>
          <h2 className="mt-4 font-khand font-700 uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl text-[#f5f5f0]">
            फॉर्म भरें,<br />WhatsApp पर भेजें
          </h2>
          <p className="mt-6 max-w-md font-hind text-neutral-300 leading-relaxed">
            नीचे विवरण भरें — “भेजें” दबाते ही आपकी बुकिंग सीधे हमारे WhatsApp पर पहुँच जाएगी। कोई झंझट नहीं, तुरंत कन्फर्मेशन।
          </p>
          <p className="mt-3 font-hind text-neutral-500 text-sm">
            Fill the form and it opens directly in WhatsApp for instant confirmation.
          </p>
        </motion.div>

        <motion.form
          data-testid="booking-form"
          onSubmit={submit}
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="bg-stone/70 backdrop-blur border border-white/10 p-8 md:p-10 space-y-7"
        >
          <div className="grid sm:grid-cols-2 gap-7">
            <div>
              <label className="font-plex text-[10px] tracking-[0.2em] uppercase text-neutral-400">नाम / Name *</label>
              <input data-testid="booking-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="आपका नाम" />
            </div>
            <div>
              <label className="font-plex text-[10px] tracking-[0.2em] uppercase text-neutral-400">फ़ोन / Phone *</label>
              <input data-testid="booking-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="10 अंकों का नंबर" inputMode="tel" />
            </div>
          </div>

          <div>
            <label className="font-plex text-[10px] tracking-[0.2em] uppercase text-neutral-400 block mb-2">सेवा / Service *</label>
            <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
              <SelectTrigger data-testid="booking-service" className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 font-hind text-[#f5f5f0] focus:ring-0 focus:border-jcb">
                <SelectValue placeholder="सेवा चुनें" />
              </SelectTrigger>
              <SelectContent className="bg-stone border-white/10 text-[#f5f5f0]">
                {BOOKING_OPTIONS.map((o) => (
                  <SelectItem key={o} value={o} className="font-hind focus:bg-jcb focus:text-black">
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid sm:grid-cols-2 gap-7">
            <div>
              <label className="font-plex text-[10px] tracking-[0.2em] uppercase text-neutral-400">तारीख / Date</label>
              <input data-testid="booking-date" type="date" value={form.date} onChange={set("date")} className={`${inputCls} [color-scheme:dark]`} />
            </div>
            <div>
              <label className="font-plex text-[10px] tracking-[0.2em] uppercase text-neutral-400">स्थान / Place</label>
              <input data-testid="booking-place" value={form.place} onChange={set("place")} className={inputCls} placeholder="Pickup / साइट" />
            </div>
          </div>

          <div>
            <label className="font-plex text-[10px] tracking-[0.2em] uppercase text-neutral-400">विवरण / Details</label>
            <textarea data-testid="booking-details" value={form.details} onChange={set("details")} rows={3} className={`${inputCls} resize-none`} placeholder="कोई अतिरिक्त जानकारी…" />
          </div>

          <button
            data-testid="booking-submit-btn"
            type="submit"
            className="group w-full flex items-center justify-center gap-3 bg-jcb text-black py-4 font-khand font-600 text-lg tracking-wide hover:bg-white transition-colors duration-200"
          >
            WhatsApp पर भेजें
            <Send size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.form>
      </div>
    </section>
  );
};
